import { Fragment, type ReactNode } from "react";
import { styled } from "@pigment-css/react";
import { ChevronRightThin } from "@acto/web-common/components";
import {
  HoverRowItem as ProductRowItem,
  HoverRowList as ProductRowList,
  InsetRowDivider as ProductRowDivider,
  RowBadge,
  RowBody,
  RowChevron,
  RowEyebrow,
  RowListCard,
  type RowListCardProps,
  RowListEmptyState,
  RowMeta,
  RowTitle,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Skeleton } from "@acto/taskpane/field-sales-2/components/primitives/Skeleton";

export interface ProductTrendItem {
  id: string;
  name: string;
  externalReference: string | null;
  hasChildren: boolean;
  canOpen?: boolean;
  formattedValue: string | null;
  value: number;
  /** Change of the selected KPI, in percent. Drives the badge tone. */
  change: number | null;
  formattedChange: string | null;
}

export interface ProductTrendListProps extends Pick<RowListCardProps, "caption" | "captionScrollRef" | "nested"> {
  items: ProductTrendItem[];
  onItemClick?: (item: ProductTrendItem) => void;
  isLoading?: boolean;
  /** Message shown in place of the list when there are no items (e.g. no groups for the timeframe). */
  emptyLabel?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}

const EMPTY_TREND_BADGE_LABEL = "-";

export function ProductTrendList({
  items,
  onItemClick,
  isLoading,
  emptyLabel,
  caption,
  captionScrollRef,
  nested,
  children,
  footer,
}: ProductTrendListProps) {
  const showSkeleton = items.length === 0 && isLoading;
  const showEmpty = items.length === 0 && !isLoading && !!emptyLabel;
  const transitionKey = showSkeleton ? "loading" : showEmpty ? "empty" : "list";

  return (
    <RowListCard
      transitionKey={transitionKey}
      header={children}
      caption={caption}
      captionScrollRef={captionScrollRef}
      nested={nested}
      footer={footer}
    >
      {showSkeleton ? (
        <ProductRowSkeletonList />
      ) : showEmpty ? (
        <RowListEmptyState>{emptyLabel}</RowListEmptyState>
      ) : (
        <ProductRowList role="list">
          {items.map((item, index) => {
            // Only groups with drillable children open; others render as plain (non-clickable) rows.
            const canOpen = !!onItemClick && (item.canOpen ?? item.hasChildren);

            return (
              <Fragment key={item.id}>
                <ProductRowItem clickable={canOpen} onClick={canOpen ? () => onItemClick?.(item) : undefined}>
                  <RowBadge tone={resolveTrendTone(item.change)}>
                    {item.formattedChange ?? EMPTY_TREND_BADGE_LABEL}
                  </RowBadge>
                  <RowBody>
                    {item.externalReference && <ProductRowEyebrow>{item.externalReference}</ProductRowEyebrow>}
                    <div>
                      <RowTitle>{item.name}</RowTitle>
                      <RowMeta>{item.formattedValue ?? "—"}</RowMeta>
                    </div>
                  </RowBody>
                  {canOpen && (
                    <RowChevron>
                      <ChevronRightThin strokeWidth={1.5} />
                    </RowChevron>
                  )}
                </ProductRowItem>
                {index < items.length - 1 && <ProductRowDivider />}
              </Fragment>
            );
          })}
        </ProductRowList>
      )}
    </RowListCard>
  );
}

export const ListTitle = styled("h2")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[18],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[900],
  paddingLeft: theme.fieldSalesTwo.spacing[16],
}));

export function resolveTrendTone(trend: number | null | undefined): "positive" | "negative" | "neutral" {
  if (trend == null || trend === 0) return "neutral";
  return trend > 0 ? "positive" : "negative";
}

const ProductRowEyebrow = styled(RowEyebrow)(({ theme }) => ({
  marginBottom: theme.fieldSalesTwo.spacing[8],
}));

const SKELETON_ROW_COUNT = 5;

// Mirrors a real product row (badge, eyebrow, title, meta) inside the same wrappers so a full page of
// skeletons matches a full page of rows in height, preventing a jump when the list enters loading.
function ProductRowSkeleton() {
  return (
    <ProductRowItem>
      <Skeleton.Box width={56} height={52} radius={16} />
      <RowBody>
        <ProductRowEyebrow>
          <Skeleton.Line inline width={72} height={12} />
        </ProductRowEyebrow>
        <div>
          <RowTitle>
            <Skeleton.Line inline width="55%" height={13} />
          </RowTitle>
          <RowMeta>
            <Skeleton.Line inline width={64} height={12} />
          </RowMeta>
        </div>
      </RowBody>
    </ProductRowItem>
  );
}

function ProductRowSkeletonList() {
  return (
    <ProductRowList role="list" aria-busy="true">
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
        <Fragment key={index}>
          <ProductRowSkeleton />
          {index < SKELETON_ROW_COUNT - 1 && <ProductRowDivider />}
        </Fragment>
      ))}
    </ProductRowList>
  );
}
